/**
 * @license
 * Copyright 2026 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as matrix from "#src/util/matrix.js";

export interface RegistrationPointPair {
  moving: Float64Array;
  fixed: Float64Array;
}

export interface RegistrationResult {
  transform: Float64Array;
  /** Physical-space affine before conversion to coordinate values, when available. */
  physicalTransform?: Float64Array;
  scale: number;
  residuals: Float64Array;
  rmsError: number;
  maxError: number;
}

const EPSILON = 1e-8;

function dot(a: ArrayLike<number>, b: ArrayLike<number>) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function norm(a: ArrayLike<number>) {
  return Math.sqrt(dot(a, a));
}

function cross(out: Float64Array, a: ArrayLike<number>, b: ArrayLike<number>) {
  const ax = a[0];
  const ay = a[1];
  const az = a[2];
  const bx = b[0];
  const by = b[1];
  const bz = b[2];
  out[0] = ay * bz - az * by;
  out[1] = az * bx - ax * bz;
  out[2] = ax * by - ay * bx;
  return out;
}

function subtract(
  out: Float64Array,
  a: ArrayLike<number>,
  b: ArrayLike<number>,
) {
  out[0] = a[0] - b[0];
  out[1] = a[1] - b[1];
  out[2] = a[2] - b[2];
  return out;
}

function validateNonCollinear(points: readonly Float64Array[]) {
  const v01 = new Float64Array(3);
  const v02 = new Float64Array(3);
  const normal = new Float64Array(3);
  for (let i = 1; i < points.length; ++i) {
    subtract(v01, points[i], points[0]);
    const v01Norm = norm(v01);
    if (!(v01Norm > 0) || !Number.isFinite(v01Norm)) continue;
    for (let j = i + 1; j < points.length; ++j) {
      subtract(v02, points[j], points[0]);
      const edgeProduct = v01Norm * norm(v02);
      if (!(edgeProduct > 0) || !Number.isFinite(edgeProduct)) continue;
      cross(normal, v01, v02);
      if (norm(normal) > EPSILON * edgeProduct) return;
    }
  }
  throw new Error("Registration points are degenerate.");
}

/** Returns the unit eigenvector for the largest eigenvalue of a symmetric 4x4 matrix. */
function largestEigenvectorSymmetric4(input: Float64Array) {
  const a = new Float64Array(input);
  const eigenvectors = matrix.createIdentity(Float64Array, 4);
  for (let iteration = 0; iteration < 50; ++iteration) {
    let p = 0;
    let q = 1;
    let maxOffDiagonal = 0;
    let maxDiagonal = 0;
    for (let row = 0; row < 4; ++row) {
      maxDiagonal = Math.max(maxDiagonal, Math.abs(a[row * 4 + row]));
      for (let col = row + 1; col < 4; ++col) {
        const value = Math.abs(a[row * 4 + col]);
        if (value > maxOffDiagonal) {
          maxOffDiagonal = value;
          p = row;
          q = col;
        }
      }
    }
    if (maxOffDiagonal <= Number.EPSILON * maxDiagonal * 16) {
      break;
    }
    const app = a[p * 4 + p];
    const aqq = a[q * 4 + q];
    const apq = a[p * 4 + q];
    const tau = (aqq - app) / (2 * apq);
    const t = (tau >= 0 ? 1 : -1) / (Math.abs(tau) + Math.sqrt(1 + tau * tau));
    const c = 1 / Math.sqrt(1 + t * t);
    const s = t * c;
    for (let k = 0; k < 4; ++k) {
      if (k === p || k === q) continue;
      const akp = a[k * 4 + p];
      const akq = a[k * 4 + q];
      const newAkp = c * akp - s * akq;
      const newAkq = s * akp + c * akq;
      a[k * 4 + p] = a[p * 4 + k] = newAkp;
      a[k * 4 + q] = a[q * 4 + k] = newAkq;
    }
    a[p * 4 + p] = c * c * app - 2 * s * c * apq + s * s * aqq;
    a[q * 4 + q] = s * s * app + 2 * s * c * apq + c * c * aqq;
    a[p * 4 + q] = a[q * 4 + p] = 0;
    for (let k = 0; k < 4; ++k) {
      const vkp = eigenvectors[k * 4 + p];
      const vkq = eigenvectors[k * 4 + q];
      eigenvectors[k * 4 + p] = c * vkp - s * vkq;
      eigenvectors[k * 4 + q] = s * vkp + c * vkq;
    }
  }
  let largest = 0;
  for (let i = 1; i < 4; ++i) {
    if (a[i * 4 + i] > a[largest * 4 + largest]) largest = i;
  }
  return Float64Array.from(
    { length: 4 },
    (_, row) => eigenvectors[row * 4 + largest],
  );
}

/** Kabsch/Horn best-fit proper rotation from centered moving to fixed points. */
function getBestFitRotation(
  movingCentered: readonly Float64Array[],
  fixedCentered: readonly Float64Array[],
) {
  const covariance = new Float64Array(9);
  for (let i = 0; i < movingCentered.length; ++i) {
    const moving = movingCentered[i];
    const fixed = fixedCentered[i];
    for (let row = 0; row < 3; ++row) {
      for (let col = 0; col < 3; ++col) {
        covariance[row * 3 + col] += moving[row] * fixed[col];
      }
    }
  }
  const [sxx, sxy, sxz, syx, syy, syz, szx, szy, szz] = covariance;
  const eigenproblem = Float64Array.of(
    sxx + syy + szz,
    syz - szy,
    szx - sxz,
    sxy - syx,
    syz - szy,
    sxx - syy - szz,
    sxy + syx,
    szx + sxz,
    szx - sxz,
    sxy + syx,
    -sxx + syy - szz,
    syz + szy,
    sxy - syx,
    szx + sxz,
    syz + szy,
    -sxx - syy + szz,
  );
  const [w, x, y, z] = largestEigenvectorSymmetric4(eigenproblem);
  const quaternionNorm = Math.hypot(w, x, y, z);
  if (!(quaternionNorm > 0) || !Number.isFinite(quaternionNorm)) {
    throw new Error("Registration points are degenerate.");
  }
  const qw = w / quaternionNorm;
  const qx = x / quaternionNorm;
  const qy = y / quaternionNorm;
  const qz = z / quaternionNorm;
  return Float64Array.of(
    1 - 2 * (qy * qy + qz * qz),
    2 * (qx * qy + qz * qw),
    2 * (qx * qz - qy * qw),
    2 * (qx * qy - qz * qw),
    1 - 2 * (qx * qx + qz * qz),
    2 * (qy * qz + qx * qw),
    2 * (qx * qz + qy * qw),
    2 * (qy * qz - qx * qw),
    1 - 2 * (qx * qx + qy * qy),
  );
}

function getUniformScale(
  movingCentered: readonly Float64Array[],
  fixedCentered: readonly Float64Array[],
  rotation: Float64Array,
) {
  let numerator = 0;
  let denominator = 0;
  for (let i = 0; i < movingCentered.length; ++i) {
    const p = movingCentered[i];
    const q = fixedCentered[i];
    const rp0 = rotation[0] * p[0] + rotation[3] * p[1] + rotation[6] * p[2];
    const rp1 = rotation[1] * p[0] + rotation[4] * p[1] + rotation[7] * p[2];
    const rp2 = rotation[2] * p[0] + rotation[5] * p[1] + rotation[8] * p[2];
    numerator += dot(q, [rp0, rp1, rp2]);
    denominator += dot(p, p);
  }
  if (!(denominator > 0) || !Number.isFinite(denominator)) {
    throw new Error("Registration points are degenerate.");
  }
  return numerator / denominator;
}

function centroid(points: readonly Float64Array[]) {
  const c = new Float64Array(3);
  for (const point of points) {
    c[0] += point[0];
    c[1] += point[1];
    c[2] += point[2];
  }
  c[0] /= points.length;
  c[1] /= points.length;
  c[2] /= points.length;
  return c;
}

function centered(points: readonly Float64Array[], center: Float64Array) {
  return points.map((point) => {
    const result = new Float64Array(3);
    subtract(result, point, center);
    return result;
  });
}

/**
 * Estimates a 3D rigid/similarity transform from at least three corresponding points.
 *
 * The returned matrix is column-major homogeneous, with columns corresponding to moving/source
 * dimensions and rows corresponding to fixed/target dimensions.
 */
export function estimateThreePointRegistration(
  pairs: readonly RegistrationPointPair[],
  allowScale: boolean,
): RegistrationResult {
  if (pairs.length < 3) {
    throw new Error("At least three point pairs are required.");
  }
  const movingPoints = pairs.map((pair) => pair.moving);
  const fixedPoints = pairs.map((pair) => pair.fixed);
  validateNonCollinear(movingPoints);
  validateNonCollinear(fixedPoints);
  const movingCenter = centroid(movingPoints);
  const fixedCenter = centroid(fixedPoints);
  const movingCentered = centered(movingPoints, movingCenter);
  const fixedCentered = centered(fixedPoints, fixedCenter);
  const rotation = getBestFitRotation(movingCentered, fixedCentered);
  const scale = allowScale
    ? getUniformScale(movingCentered, fixedCentered, rotation)
    : 1;
  if (!(scale > 0) || !Number.isFinite(scale)) {
    throw new Error("Registration scale is invalid.");
  }
  const transform = matrix.createIdentity(Float64Array, 4);
  for (let row = 0; row < 3; ++row) {
    for (let col = 0; col < 3; ++col) {
      transform[col * 4 + row] = scale * rotation[col * 3 + row];
    }
    transform[3 * 4 + row] =
      fixedCenter[row] -
      scale *
        (rotation[0 * 3 + row] * movingCenter[0] +
          rotation[1 * 3 + row] * movingCenter[1] +
          rotation[2 * 3 + row] * movingCenter[2]);
  }
  const residuals = new Float64Array(pairs.length);
  let squaredSum = 0;
  let maxError = 0;
  for (let i = 0; i < pairs.length; ++i) {
    const moving = movingPoints[i];
    const fixed = fixedPoints[i];
    const dx =
      transform[0] * moving[0] +
      transform[4] * moving[1] +
      transform[8] * moving[2] +
      transform[12] -
      fixed[0];
    const dy =
      transform[1] * moving[0] +
      transform[5] * moving[1] +
      transform[9] * moving[2] +
      transform[13] -
      fixed[1];
    const dz =
      transform[2] * moving[0] +
      transform[6] * moving[1] +
      transform[10] * moving[2] +
      transform[14] -
      fixed[2];
    const error = Math.sqrt(dx * dx + dy * dy + dz * dz);
    residuals[i] = error;
    squaredSum += error * error;
    maxError = Math.max(maxError, error);
  }
  return {
    transform,
    scale,
    residuals,
    rmsError: Math.sqrt(squaredSum / pairs.length),
    maxError,
  };
}

/**
 * Fits in physical space and converts the resulting transform back to coordinate values.
 *
 * For coordinate-to-physical scale matrix `S`, the returned affine is
 * `S^-1 * physicalTransform * S`.
 */
export function estimateThreePointRegistrationWithCoordinateScales(
  pairs: readonly RegistrationPointPair[],
  coordinateScales: ArrayLike<number>,
  allowScale: boolean,
): RegistrationResult {
  if (coordinateScales.length !== 3) {
    throw new Error("Exactly three coordinate scales are required.");
  }
  const scales = Float64Array.from(coordinateScales);
  for (const scale of scales) {
    if (!(scale > 0) || !Number.isFinite(scale)) {
      throw new Error(
        "Registration coordinate scales must be finite and positive.",
      );
    }
  }
  const physicalPairs = pairs.map(({ moving, fixed }) => ({
    moving: Float64Array.from(moving, (value, i) => value * scales[i]),
    fixed: Float64Array.from(fixed, (value, i) => value * scales[i]),
  }));
  const physicalResult = estimateThreePointRegistration(
    physicalPairs,
    allowScale,
  );
  const transform = new Float64Array(physicalResult.transform);
  for (let row = 0; row < 3; ++row) {
    for (let col = 0; col < 3; ++col) {
      transform[col * 4 + row] *= scales[col] / scales[row];
    }
    transform[12 + row] /= scales[row];
  }
  return {
    ...physicalResult,
    transform,
    physicalTransform: physicalResult.transform,
  };
}
